import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { rehydrateEmail as rehydrateEmailRequest, markEmailRead } from '@/api/emails';
import { setOptimisticFlags, clearOptimisticFlags } from '@/stores/emailStateHelpers';
import { executeEmailAction, dispatchOrQueue } from '@/stores/emailCommands';
import { fetchAutoLabels, appendAutoLabelTarget, removeMessageAutoLabel, type AutoLabelTargetInput } from '@/api/autoLabels';
import type { Email, EmailActionContext, ThreadMessage, AutoLabelDefinition } from '@/types/email';
import type { ReplyContext } from '@/types/ui';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AutoLabelBadge } from '@/components/AutoLabelBadge';
import { AlertCircle, AlertTriangle, ShieldCheck, ShieldOff, Mail, MailOpen, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBrandfetchLogoUrl } from '@/lib/avatar';
import { DetailHeader } from './components/DetailHeader';
import { MessageMetadata, type ReplyMode } from './components/MessageMetadata';
import { AiSummaryCard } from './components/AiSummaryCard';
import { InlineImageControls } from './components/InlineImageControls';
import { AttachmentList } from './components/AttachmentList';
import { MessageBody } from './components/MessageBody';
import { CalendarInvitation } from './components/CalendarInvitation';
import { InlineReplyComposer } from './components/InlineReplyComposer';
import { MobileActionBar } from './components/MobileActionBar';
import { EmailTaskCreationProvider } from './components/EmailTaskCreation';
import { EmailEventCreationProvider } from './components/EmailEventCreation';
import { EmailProductLineCreationProvider } from './components/EmailProductLineCreation';
import { useInlineAttachments } from './hooks/useInlineAttachments';
import { useRemoteImages } from './hooks/useRemoteImages';
import { useTrainMessage } from '@/hooks/useTrainMessage';
import { useMessageSummary } from '@/hooks/useMessageSummary';
import { sanitizeMessageHtml } from './utils/htmlSanitizer';
import { buildReplyContextWithFallback } from './utils/replyContext';
import { useTodoStore } from '@/stores/todoStore';
import { useTodoActionsStore } from '@/stores/todoActionsStore';
import { useEmailStore } from '@/stores/emailStore';
import { useFolderStore } from '@/stores/folderStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import type { TodoTask } from '@/types/todo';
import { TodoTaskCard } from '@/features/todo/components/TodoTaskCard';
import { useAutoLabelStore } from '@/stores/autoLabelStore';
import { useAccountStore } from '@/stores/accountStore';
import { useUIStore } from '@/stores/uiStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { AddSenderToLabelDialog } from '@/components/AddSenderToLabelDialog';
import { RelatedTasks } from './components/RelatedTasks';

type EmailLike = Email | ThreadMessage;

interface OcrMetadataSummary {
  attachmentsProcessed?: number;
  attachmentsConsidered?: number;
  durationMs?: number;
  failures?: number;
  reason: string | null;
  skippedReasons: Record<string, number>;
  totalSegments?: number;
  processedSegments?: number;
  totalBytes?: number;
  languages: string[];
}

interface EmailDetailProps {
  email: EmailLike | null;
  loading?: boolean;
  onBack?: () => void;
  onDelete: (emailId: string, context?: EmailActionContext) => void;
  onArchive: (emailId: string, context?: EmailActionContext) => void;
  onEmailUpdate?: () => void;
  onReply?: (context: ReplyContext) => void;
  accountId: string | null;
  mode?: 'standalone' | 'embedded';
  align?: 'start' | 'end';
  highlighted?: boolean;
  context?: 'thread' | 'participant' | null;
  onApproveSender?: (contactId: string) => void;
  onBlockSender?: (contactId: string) => void;
  operationPending?: boolean;
  isSent?: boolean;
}

const mergeAutoLabels = (
  current: Email['autoLabels'] = [],
  next: Email['autoLabels'][number],
): Email['autoLabels'] => {
  const existingIndex = current.findIndex((label) => label.id === next.id);
  if (existingIndex >= 0) {
    const updated = [...current];
    updated[existingIndex] = { ...updated[existingIndex], ...next };
    return updated;
  }
  return [...current, next];
};

export const EmailDetail = React.memo(function EmailDetail({
  email: initialEmail,
  loading,
  onBack,
  onDelete,
  onArchive,
  onEmailUpdate,
  onReply,
  accountId,
  mode = 'standalone',
  align = 'start',
  highlighted = false,
  context,
  onApproveSender,
  onBlockSender,
  operationPending = false,
  isSent = false,
}: EmailDetailProps) {
  const [localOverrides, setLocalOverrides] = useState<Partial<EmailLike> | null>(null);
  const email = useMemo(
    () => localOverrides ? { ...initialEmail, ...localOverrides } as EmailLike : initialEmail,
    [initialEmail, localOverrides],
  );
  const isParticipantContext = context === 'participant';
  const isForwardedEmail = /^fwd:/i.test(email?.subject ?? '');

  const remoteImages = useRemoteImages(email);
  const attachmentsState = useInlineAttachments(email, accountId);
  const {
    trainAsSpam,
    trainAsGood,
    isTrainingSpam,
    isTrainingGood,
    canTrainSpam,
    canTrainGood,
  } = useTrainMessage(email, { source: 'mobile_action_bar' });
  const updateEmailInStore = useEmailStore((state) => state.updateEmail);
  const updateEmailInPlace = useEmailStore((state) => state.updateEmailInPlace);
  const updateDigestEmail = useSenderGroupingStore((state) => state.updateDigestEmail);
  const labelMap = useAutoLabelStore((state) => state.labels);
  const labelOrder = useAutoLabelStore((state) => state.labelOrder);
  const labelsInitialized = useAutoLabelStore((state) => state.initialized);
  const labelsVisible = useUIStore((state) => state.labelsVisible);
  const setLabels = useAutoLabelStore((state) => state.setLabels);
  const upsertLabel = useAutoLabelStore((state) => state.upsertLabel);
  const accounts = useAccountStore((state) => state.accounts);

  // Set of user's own email addresses (lowercase) for filtering CC on Reply All
  const accountEmails = useMemo(() => {
    const emails = new Set<string>();
    accounts.forEach((account) => {
      if (account.emailAddress) {
        emails.add(account.emailAddress.toLowerCase());
      }
    });
    return emails;
  }, [accounts]);

  const senderLabels = useMemo(() => {
    return labelOrder
      .map((id) => labelMap[id])
      .filter((label): label is AutoLabelDefinition => Boolean(label) && label.type === 'sender');
  }, [labelMap, labelOrder]);
  useEffect(() => {
    if (labelsInitialized) {
      return;
    }
    let cancelled = false;
    fetchAutoLabels()
      .then((fetched) => {
        if (!cancelled) {
          setLabels(fetched);
        }
      })
      .catch((error) => {
        console.error('Failed to load labels', error);
      });
    return () => {
      cancelled = true;
    };
  }, [labelsInitialized, setLabels]);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string>('');
  const [labelDialogError, setLabelDialogError] = useState<string | null>(null);
  const [labelDialogPending, setLabelDialogPending] = useState(false);
  const [ocrCopyState, setOcrCopyState] = useState<'idle' | 'copied'>('idle');
  const [isOcrVisible, setOcrVisible] = useState(false);
  useEffect(() => {
    setLocalOverrides(null);
  }, [initialEmail?.id]);
  useEffect(() => {
    if (ocrCopyState !== 'copied') {
      return undefined;
    }
    const timeout = setTimeout(() => setOcrCopyState('idle'), 2000);
    return () => clearTimeout(timeout);
  }, [ocrCopyState]);
  const [rehydrating, setRehydrating] = useState(false);
  const handleRehydrateEmail = useCallback(
    async (targetEmailId: string) => {
      if (!accountId) {
        return;
      }
      setRehydrating(true);
      try {
        const refreshed = await rehydrateEmailRequest(accountId, targetEmailId);
        updateEmailInStore(refreshed);
      } catch (error) {
        console.error('Failed to re-fetch email', error);
      } finally {
        setRehydrating(false);
      }
    },
    [accountId, updateEmailInStore],
  );
  const canRehydrate = Boolean(accountId && email);

  const emailBodyHtml = email?.bodyHtml;
  const emailBody = email?.body;
  const sanitizedHtml = useMemo(() => {
    if (emailBodyHtml) {
      return sanitizeMessageHtml(emailBodyHtml, attachmentsState.cidMap, {
        showRemoteImages: remoteImages.showRemoteImages,
      });
    }
    // Fall back to plain text body if no HTML
    if (emailBody && emailBody.trim()) {
      const escaped = emailBody
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      // Plain text carries no sender colours, so it follows the app theme.
      return {
        styles: '',
        body: `<div style="white-space: pre-wrap;">${escaped}</div>`,
        bodyAttributes: '',
        supportsDarkMode: true,
      };
    }
    return null;
  }, [emailBodyHtml, emailBody, attachmentsState.cidMap, remoteImages.showRemoteImages]);
  const hasOcrText = Boolean(email?.ocrText && email.ocrText.trim().length);
  const ocrStatusLabel = email?.ocrStatus || (hasOcrText ? 'complete' : null);
  const ocrMetadataSummary = useMemo<OcrMetadataSummary | null>(() => {
    const metadata = email?.ocrMetadata as Record<string, unknown> | null | undefined;
    if (!metadata) {
      return null;
    }
    const toNumber = (value: unknown) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };
    const toReason = (value: unknown) => {
      if (typeof value !== 'string') {
        return null;
      }
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    };
    const attachmentsProcessed = toNumber(metadata['attachmentsProcessed']);
    const attachmentsConsidered = toNumber(metadata['attachmentsConsidered']);
    const durationMs = toNumber(metadata['durationMs']);
    const totalBytes = toNumber(metadata['totalBytes']);
    const failures = Array.isArray(metadata['failures']) ? metadata['failures'].length : undefined;
    const segments = Array.isArray(metadata['segments'])
      ? metadata['segments'].filter(
          (entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object',
        )
      : [];
    const skippedReasons = segments.reduce<Record<string, number>>((acc, segment) => {
      const reason = toReason(segment['reason']);
      if (reason) {
        acc[reason] = (acc[reason] ?? 0) + 1;
      }
      return acc;
    }, {});
    const processedSegments = segments.filter((segment) => !toReason(segment['reason'])).length;
    const totalSegments = segments.length;
    const languages = Array.isArray(metadata['languages'])
      ? metadata['languages']
          .map((lang) => (typeof lang === 'string' ? lang.trim() : ''))
          .filter(Boolean)
      : [];
    const reason = toReason(metadata['reason']);
    return {
      attachmentsProcessed,
      attachmentsConsidered,
      durationMs,
      totalBytes,
      failures,
      reason,
      skippedReasons,
      processedSegments,
      totalSegments,
      languages,
    };
  }, [email?.ocrMetadata]);
  const hasOcrMetadata = useMemo(() => {
    if (!ocrMetadataSummary) {
      return false;
    }
    return (
      Boolean(ocrMetadataSummary.reason) ||
      Object.keys(ocrMetadataSummary.skippedReasons ?? {}).length > 0 ||
      typeof ocrMetadataSummary.attachmentsProcessed === 'number' ||
      typeof ocrMetadataSummary.attachmentsConsidered === 'number' ||
      typeof ocrMetadataSummary.totalSegments === 'number' ||
      typeof ocrMetadataSummary.totalBytes === 'number' ||
      typeof ocrMetadataSummary.durationMs === 'number'
    );
  }, [ocrMetadataSummary]);
  const showOcrSection = Boolean(hasOcrText || ocrStatusLabel || hasOcrMetadata);
  const ocrProcessedLabel = useMemo(() => {
    if (!email?.ocrProcessedAt) {
      return null;
    }
    const parsed = new Date(email.ocrProcessedAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed.toLocaleString();
  }, [email?.ocrProcessedAt]);
  const sanitizedOcrText = useMemo(() => (email?.ocrText ? email.ocrText.trim() : ''), [email?.ocrText]);
  const handleViewOcrData = useCallback(() => {
    if (!showOcrSection) {
      return;
    }
    setOcrVisible(true);
  }, [showOcrSection]);
  const handleHideOcrData = useCallback(() => {
    setOcrVisible(false);
  }, []);
  const handleCopyOcrText = useCallback(async () => {
    if (!sanitizedOcrText) {
      return;
    }
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) {
        return;
      }
      await navigator.clipboard.writeText(sanitizedOcrText);
      setOcrCopyState('copied');
    } catch (error) {
      console.error('Failed to copy OCR text', error);
      setOcrCopyState('idle');
    }
  }, [sanitizedOcrText]);
  const ocrHeadingLabel = hasOcrText ? 'Extracted text from images' : 'Image OCR result';
  const ocrReasonLabel = useMemo(() => formatOcrReason(ocrMetadataSummary?.reason), [ocrMetadataSummary?.reason]);
  const ocrSkippedBreakdown = useMemo(
    () => formatReasonCounts(ocrMetadataSummary?.skippedReasons ?? {}),
    [ocrMetadataSummary],
  );
  const ocrDetailsLine = useMemo(() => {
    if (!ocrMetadataSummary) {
      return null;
    }
    const processedCount =
      ocrMetadataSummary.attachmentsProcessed ??
      (typeof ocrMetadataSummary.processedSegments === 'number'
        ? ocrMetadataSummary.processedSegments
        : undefined) ??
      (hasOcrText ? 1 : undefined);
    const consideredCount = ocrMetadataSummary.attachmentsConsidered ?? ocrMetadataSummary.totalSegments;
    const parts: string[] = [];
    if (processedCount !== undefined || consideredCount !== undefined) {
      parts.push(`Images: ${processedCount ?? 0}${consideredCount ? ` / ${consideredCount}` : ''}`);
    }
    if (typeof ocrMetadataSummary.totalBytes === 'number') {
      parts.push(`${(ocrMetadataSummary.totalBytes / 1024).toFixed(1)} KB total`);
    }
    if (ocrSkippedBreakdown) {
      parts.push(`Skipped: ${ocrSkippedBreakdown}`);
    } else if (ocrReasonLabel && !hasOcrText) {
      parts.push(`Reason: ${ocrReasonLabel}`);
    }
    if (typeof ocrMetadataSummary.durationMs === 'number') {
      parts.push(`${(ocrMetadataSummary.durationMs / 1000).toFixed(1)}s`);
    }
    if (ocrMetadataSummary.languages.length) {
      parts.push(`Languages: ${ocrMetadataSummary.languages.join(', ')}`);
    }
    if (ocrMetadataSummary.failures) {
      parts.push(`${ocrMetadataSummary.failures} error${ocrMetadataSummary.failures === 1 ? '' : 's'}`);
    }
    return parts.length ? parts.join(' · ') : null;
  }, [hasOcrText, ocrMetadataSummary, ocrReasonLabel, ocrSkippedBreakdown]);
  const ocrStatusLine = useMemo(() => {
    const parts: string[] = [];
    if (ocrStatusLabel) {
      parts.push(`Status: ${ocrStatusLabel}`);
    } else {
      parts.push('Status: pending');
    }
    if (ocrProcessedLabel) {
      parts.push(`Updated ${ocrProcessedLabel}`);
    }
    return parts.join(' · ');
  }, [ocrProcessedLabel, ocrReasonLabel, ocrStatusLabel]);
  const ocrEmptyStateMessage = useMemo(() => {
    if (hasOcrText) {
      return '';
    }
    const processedCount =
      ocrMetadataSummary?.attachmentsProcessed ?? ocrMetadataSummary?.processedSegments ?? 0;
    const consideredCount =
      ocrMetadataSummary?.attachmentsConsidered ?? ocrMetadataSummary?.totalSegments ?? null;
    const hasConsidered = typeof consideredCount === 'number' && consideredCount > 0;
    const detailShowedSkipInfo = Boolean(
      ocrSkippedBreakdown || (!hasOcrText && ocrReasonLabel && !ocrSkippedBreakdown),
    );
    const skippedDetailParts: string[] = [];
    if (ocrSkippedBreakdown) {
      skippedDetailParts.push(ocrSkippedBreakdown);
      if (
        ocrReasonLabel &&
        !ocrSkippedBreakdown.toLowerCase().includes(ocrReasonLabel.toLowerCase())
      ) {
        skippedDetailParts.push(ocrReasonLabel);
      }
    } else if (ocrReasonLabel) {
      skippedDetailParts.push(ocrReasonLabel);
    }
    const skippedDetails = skippedDetailParts.join(' — ');
    if (ocrStatusLabel && ocrStatusLabel.toLowerCase() === 'skipped') {
      if (skippedDetails && !detailShowedSkipInfo) {
        return `OCR skipped: ${skippedDetails}.`;
      }
      return 'OCR was skipped for this email.';
    }
    if (skippedDetails && (processedCount === 0 || hasConsidered) && !detailShowedSkipInfo) {
      return `No image text extracted. ${ocrReasonLabel ? 'Reason:' : 'Skipped because'} ${skippedDetails}.`;
    }
    if (ocrMetadataSummary && processedCount === 0 && hasConsidered) {
      return 'No image text was extracted from the images we scanned.';
    }
    return 'Image text is still processing.';
  }, [hasOcrText, ocrMetadataSummary, ocrReasonLabel, ocrSkippedBreakdown, ocrStatusLabel]);
  const canViewOcrData = showOcrSection;
  const ocrPanel = showOcrSection && isOcrVisible ? (
    <div className="rounded-lg border bg-muted/30">
      <div className="flex flex-col gap-2 border-b px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium">{ocrHeadingLabel}</p>
          <p className="text-xs text-muted-foreground">{ocrStatusLine}</p>
          {ocrDetailsLine ? <p className="text-xs text-muted-foreground">{ocrDetailsLine}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {ocrStatusLabel && (
            <span className="rounded-full border px-2 py-0.5 text-xs capitalize text-muted-foreground">
              {ocrStatusLabel}
            </span>
          )}
          {hasOcrText && (
            <Button variant="ghost" size="sm" onClick={handleCopyOcrText}>
              <Copy className="mr-2 h-4 w-4" />
              {ocrCopyState === 'copied' ? 'Copied' : 'Copy'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleHideOcrData}>
            Hide
          </Button>
        </div>
      </div>
      {hasOcrText ? (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap px-4 py-3 text-sm font-mono">
          {sanitizedOcrText}
        </pre>
      ) : (
        <div className="px-4 py-3 text-sm text-muted-foreground">{ocrEmptyStateMessage}</div>
      )}
    </div>
  ) : null;

  const summary = useMessageSummary(email);
  const {
    annotation: summaryAnnotation,
    quickSummary: summaryQuickSummary,
    isLoading: summaryLoading,
    error: summaryError,
    isSupported: summarySupported,
    requestSummary,
  } = summary;
  const [isSummaryVisible, setSummaryVisible] = useState(false);

  const [inlineReplyContext, setInlineReplyContext] = useState<ReplyContext | null>(null);

  // D9: the open message belongs to a conversation whose fetch failed — say so
  // inline with a Retry instead of silently degrading to single-message view.
  const threadLoadFailed = useEmailStore((state) => state.selectedThreadLoadFailed);
  const selectedEmailIdInStore = useEmailStore((state) => state.selectedEmailId);
  const showThreadLoadError =
    mode === 'standalone' &&
    threadLoadFailed &&
    Boolean(initialEmail?.id) &&
    selectedEmailIdInStore === initialEmail?.id;
  const handleRetryThread = useCallback(() => {
    if (!initialEmail?.id) {
      return;
    }
    void useEmailStore.getState().selectEmail(
      initialEmail.id,
      initialEmail.accountId ?? accountId ?? 'all',
      initialEmail.folderId ?? '',
      { threadId: initialEmail.threadId ?? null }
    );
  }, [initialEmail?.id, initialEmail?.accountId, initialEmail?.folderId, initialEmail?.threadId, accountId]);

  // Identity anchor for late async responses: EmailDetail is one persistent
  // (unkeyed) instance, so every .then that writes component state must verify
  // the email it was started for is still the one displayed.
  const currentEmailIdRef = useRef<string | null>(null);

  useEffect(() => {
    currentEmailIdRef.current = email?.id ?? null;
    setInlineReplyContext(null);
  }, [email?.id]);

  useEffect(() => {
    // Show summary card by default if we have a quick summary or complete summary
    const hasAnySummary = Boolean(summaryQuickSummary) || Boolean(summaryAnnotation);
    setSummaryVisible(hasAnySummary);
  }, [email?.id, summaryQuickSummary, summaryAnnotation]);

  useEffect(() => {
    setOcrVisible(false);
  }, [email?.id]);

  // Track which emails have been auto-marked as read (to avoid duplicate API calls)
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const accountFolderCache = useFolderStore((state) => state.accountFolderCache);

  // Auto-mark email as read when opened (standalone mode only)
  useEffect(() => {
    // Use the email's own accountId for API calls (important for unified inbox)
    const emailAccountId = initialEmail?.accountId ?? accountId;

    // If folderId is null (e.g., from sender digest), try to find the inbox folder for this account
    let effectiveFolderId = initialEmail?.folderId;
    if (!effectiveFolderId && emailAccountId && accountFolderCache[emailAccountId]) {
      const accountFolders = accountFolderCache[emailAccountId];
      const inboxFolder = accountFolders.find((f) => f.role === 'inbox');
      effectiveFolderId = inboxFolder?.id ?? null;
    }

    if (
      mode !== 'standalone' ||
      // Review finding 6: initialEmail is now the SYNCHRONOUS optimistic seed
      // (P14), so gate auto-mark-read on a CONFIRMED open — `loading` is true
      // during the seed window and only flips false once the fetch commits (and
      // never flips for a scrubbed-past j/k neighbor whose request was
      // superseded). This restores "read" meaning the content actually loaded,
      // and stops marking read rows the user merely scrubbed over or that 404.
      loading ||
      !initialEmail ||
      !emailAccountId ||
      !effectiveFolderId ||
      initialEmail.read !== false ||
      initialEmail.draft
    ) {
      return;
    }

    // Skip if we've already marked this email as read
    if (markedAsReadRef.current.has(initialEmail.id)) {
      return;
    }

    // Mark that we're processing this email to prevent duplicate calls
    markedAsReadRef.current.add(initialEmail.id);

    // Record the optimistic read in the dirty overlay BEFORE the API call: this
    // suppresses the echo (the backend may emit before the HTTP response) AND
    // survives a loadEmails reset so the email doesn't revert to unread on refresh.
    setOptimisticFlags(initialEmail.id, { read: true });

    // Mark as read in the backend — through the shared offline-aware
    // dispatcher, so a dead connection parks a replay instead of rolling back.
    console.log('[EmailDetail] auto-mark: calling markEmailRead', initialEmail.id);
    void dispatchOrQueue(
      {
        op: 'markRead',
        accountId: emailAccountId,
        folderId: effectiveFolderId,
        emailId: initialEmail.id,
        read: true,
      },
      [initialEmail.id],
      () => markEmailRead(emailAccountId, initialEmail.id, true, effectiveFolderId)
    )
      .then(() => {
        console.log('[EmailDetail] auto-mark: markEmailRead succeeded', initialEmail.id);
        // Only update the read field — do NOT spread initialEmail as it may have
        // stale bodyHtml that would overwrite the fresh content from selectEmail
        updateEmailInPlace(initialEmail.id, { read: true });
        updateDigestEmail(initialEmail.id, { read: true });
        // localOverrides belong to the DISPLAYED email — a late response for a
        // previous message must not stamp its read state onto the current one.
        if (currentEmailIdRef.current === initialEmail.id) {
          setLocalOverrides((prev) => ({ ...prev, read: true }));
        }
        // Note: We intentionally don't call onEmailUpdate() here as that would trigger
        // a full mailbox reload which disrupts sender group views. The local state
        // updates above are sufficient for the UI.
      })
      .catch((error) => {
        console.error('Failed to auto-mark email as read:', error);
        // Drop the optimistic overlay so server truth flows, and allow a retry.
        clearOptimisticFlags(initialEmail.id);
        markedAsReadRef.current.delete(initialEmail.id);
      });
  }, [initialEmail, accountId, accountFolderCache, mode, loading, updateEmailInPlace, updateDigestEmail]);

  const handleSummarizeClick = useCallback(() => {
    if (!summarySupported) {
      return;
    }
    setSummaryVisible(true);
    if (!summaryAnnotation && !summaryLoading) {
      void requestSummary();
    }
  }, [summaryAnnotation, summaryLoading, summarySupported, requestSummary]);

  const summaryAction = summarySupported && mode !== 'embedded' ? handleSummarizeClick : undefined;

  const handleCopyAiPayload = useCallback(async () => {
    if (!email) {
      return;
    }
    try {
      const payload = buildAiDebugPayload(email);
      await copyTextToClipboard(payload);
    } catch (error) {
      console.error('Failed to copy AI payload for debugging', error);
    }
  }, [email]);

  const copyAiPayloadAction = email ? handleCopyAiPayload : undefined;

  const addressTooltipEntries = useMemo(() => {
    if (!email) {
      return [];
    }
    const entries: Array<{ label: string; value: string }> = [];
    const fromValue = formatAddressDisplay(email.from);
    if (fromValue) {
      entries.push({ label: 'From', value: fromValue });
    }
    const toValue = formatAddressList(email.to);
    if (toValue) {
      entries.push({ label: 'To', value: toValue });
    }
    const ccValue = formatAddressList(email.cc);
    if (ccValue) {
      entries.push({ label: 'Cc', value: ccValue });
    }
    const bccValue = formatAddressList(email.bcc);
    if (bccValue) {
      entries.push({ label: 'Bcc', value: bccValue });
    }
    return entries;
  }, [email]);

  const messageAddressEntries = useMemo(() => {
    if (!email || isParticipantContext) {
      return [];
    }
    return addressTooltipEntries.filter((entry) => entry.label !== 'From');
  }, [addressTooltipEntries, email, isParticipantContext]);

  const encryption = useMemo(() => extractEncryptionFlags(email), [email]);
  const unsubscribeUrl = email?.unsubscribeUrl ?? null;

  const senderAvatarSrc = email?.from?.email ? getBrandfetchLogoUrl(email.from.email) : null;
  const contactId = email?.contactId ?? null;
  const contactApprovalState = email?.contactApprovalState ?? null;
  const contactBadgeVariant =
    contactApprovalState === 'approved'
      ? 'default'
      : contactApprovalState === 'blocked'
        ? 'destructive'
        : 'secondary';
  const canApproveSender = Boolean(contactId && onApproveSender);
  const canBlockSender = Boolean(contactId && onBlockSender);

  // Block sender - only for approved contacts
  const canBlockApprovedSender = useMemo(() =>
    Boolean(
      contactId &&
      onBlockSender &&
      contactApprovalState === 'approved'
    ),
    [contactId, onBlockSender, contactApprovalState]
  );

  const handleBlockApprovedSender = useCallback(() => {
    if (contactId && onBlockSender && contactApprovalState === 'approved') {
      onBlockSender(contactId);
    }
  }, [contactId, onBlockSender, contactApprovalState]);

  const contactNoticeVisible =
    !!email &&
    contactApprovalState &&
    contactApprovalState !== 'approved';

  const inlineImageControlsVisible =
    !!email &&
    remoteImages.isBlockedSender &&
    Boolean(email.bodyHtml) &&
    !remoteImages.showRemoteImages;

  const handleReplyAction = useCallback(
    async (modeType: ReplyMode) => {
      if (!email) {
        return;
      }
      const requestEmailId = email.id;
      const context = await buildReplyContextWithFallback(email, modeType, { sanitizedHtml: sanitizedHtml?.body ?? null, accountEmails });
      if (!context) {
        return;
      }
      // Identity guard: the context build can fetch; if the user navigated to
      // another email meanwhile, the composer must NOT open under it (it would
      // reply to the wrong thread).
      if (currentEmailIdRef.current !== requestEmailId) {
        return;
      }
      if (!accountId && onReply) {
        onReply(context);
        return;
      }
      setInlineReplyContext(context);
    },
    [accountId, email, onReply, sanitizedHtml?.body, accountEmails],
  );

  // Handle keyboard-triggered reply/forward via store signal
  const pendingReply = useUIStore((s) => s.pendingReply);
  useEffect(() => {
    if (pendingReply && email && pendingReply.emailId === email.id) {
      handleReplyAction(pendingReply.mode);
      useUIStore.getState().setPendingReply(null);
    }
  }, [pendingReply, email, handleReplyAction]);

  const handleOpenFullComposer = useCallback(
    (context: ReplyContext) => {
      onReply?.(context);
    },
    [onReply],
  );

  const handleApproveSender = useCallback(() => {
    if (contactId && onApproveSender) {
      onApproveSender(contactId);
    }
  }, [contactId, onApproveSender]);

  const handleBlockSender = useCallback(() => {
    if (contactId && onBlockSender) {
      onBlockSender(contactId);
    }
  }, [contactId, onBlockSender]);

  const handleCalendarEventUpdate = useCallback(() => {
    onEmailUpdate?.();
  }, [onEmailUpdate]);

  const handleOpenLabelDialog = useCallback(() => {
    if (senderLabels.length && !selectedLabelId) {
      setSelectedLabelId(senderLabels[0].id);
    }
    setLabelDialogError(null);
    setLabelDialogOpen(true);
  }, [senderLabels, selectedLabelId]);

  const handleAddLabelConfirm = useCallback(async () => {
    if (!email || !selectedLabelId) {
      return;
    }
    const senderEmail = email.from?.email;
    if (!senderEmail) {
      setLabelDialogError('Sender email unavailable');
      return;
    }
    const targetPayload: AutoLabelTargetInput = {
      matchType: (email.contactId ? 'contact' : 'email') as 'contact' | 'email',
      contactId: email.contactId ?? undefined,
      value: email.contactId ? undefined : senderEmail,
    };
    setLabelDialogPending(true);
    setLabelDialogError(null);
    try {
      const updatedTargets = await appendAutoLabelTarget(selectedLabelId, targetPayload, {
        accountId: email.accountId,
        messageId: email.id,
        senderEmail,
        contactState: email.contactApprovalState ?? null,
      });
      const labelDef = senderLabels.find((label) => label.id === selectedLabelId);
      if (labelDef) {
        upsertLabel({ ...labelDef, targets: updatedTargets });
        const nextAssignment = {
          id: labelDef.id,
          name: labelDef.name,
          type: 'sender',
          source: 'sender',
          reason: null,
          color: labelDef.color ?? null,
        } as Email['autoLabels'][number];
        const mergedLabels = mergeAutoLabels(email.autoLabels || [], nextAssignment);
        // localOverrides belongs to the DISPLAYED email — after navigation the
        // late response must not stamp the previous email's labels onto the
        // new one (D12 sibling, N22). The store write is id-keyed and safe.
        if (currentEmailIdRef.current === email.id) {
          setLocalOverrides((prev) => ({ ...prev, autoLabels: mergedLabels }));
        }
        updateEmailInStore({ id: email.id, autoLabels: mergedLabels });
      }
      setLabelDialogOpen(false);
    } catch (error) {
      setLabelDialogError((error as Error)?.message ?? 'Failed to update label');
    } finally {
      setLabelDialogPending(false);
    }
  }, [email, selectedLabelId, senderLabels, updateEmailInStore, upsertLabel]);

  const handleRemoveLabel = useCallback(
    async (labelId: string) => {
      if (!email?.accountId || !email?.id) {
        return;
      }
      const updatedLabels = (email.autoLabels || []).filter((l) => l.id !== labelId);
      try {
        await removeMessageAutoLabel(email.accountId, email.id, labelId);
        // Guarded like the auto-mark write (D12 sibling, N22): only override
        // the render if this email is still the one on screen.
        if (currentEmailIdRef.current === email.id) {
          setLocalOverrides((prev) => ({ ...prev, autoLabels: updatedLabels }));
        }
        // Update email store (for main list view)
        updateEmailInStore({
          id: email.id,
          autoLabels: updatedLabels,
        });
        // Update sender digest (for sender group view)
        updateDigestEmail(email.id, { autoLabels: updatedLabels });
      } catch (error) {
        console.error('Failed to remove label', error);
      }
    },
    [email, updateEmailInStore, updateDigestEmail],
  );

  const emailActionContext = useMemo<EmailActionContext>(
    () => ({
      accountId: email?.accountId ?? null,
      folderId: email?.folderId ?? null,
    }),
    [email?.accountId, email?.folderId],
  );

  const todoTasks = useTodoStore((state) => state.tasks);
  const todoTaskOrder = useTodoStore((state) => state.taskOrder);

  const linkedTasks = useMemo(() => {
    if (!email) {
      return [];
    }
    return todoTaskOrder
      .map((id) => todoTasks[id])
      .filter(
        (task): task is TodoTask =>
          Boolean(task?.emails.some((link) => link.accountId === email.accountId && link.messageId === email.id))
      );
  }, [email, todoTasks, todoTaskOrder]);



  const handleArchive = useCallback(
    (targetId: string) => {
      onArchive(targetId, emailActionContext);
    },
    [emailActionContext, onArchive],
  );

  const handleDelete = useCallback(
    (targetId: string) => {
      onDelete(targetId, emailActionContext);
    },
    [emailActionContext, onDelete],
  );

  const handleMoveToFolder = useCallback(() => {
    if (email?.id) {
      useMoveToFolderStore.getState().open([email.id]);
    }
  }, [email?.id]);

  const handleMarkRead = useCallback(
    async (targetId: string, read: boolean) => {
      // In-detail action = per-MESSAGE semantics ('messages' target — never
      // thread-expanded). The engine resolves the real (account, folder) via
      // resolveEmailContext, so digest-opened emails without a folderId no
      // longer silently no-op; it also owns the optimistic overlay + rollback.
      setLocalOverrides((prev) => ({ ...prev, read }));
      const result = await executeEmailAction(read ? 'markRead' : 'markUnread', {
        kind: 'messages',
        ids: [targetId],
      });
      if (result.failedIds.includes(targetId)) {
        // Engine already restored store state + toasted; roll back the local
        // render override too — but only if this email is still displayed.
        if (currentEmailIdRef.current === targetId) {
          setLocalOverrides((prev) => ({ ...prev, read: !read }));
        }
        return;
      }
      onEmailUpdate?.();
    },
    [onEmailUpdate],
  );

  // P14 (PERF_AUDIT): only show the full-pane placeholder when we have NOTHING to
  // render. When the store seeds an optimistic header from the cached list row,
  // fall through and render the chrome immediately — the body region skeletons
  // (below) until the fetched HTML lands, so perceived latency is "instant header"
  // instead of a blank pane for a full round trip.
  if (loading && !email) {
    return (
      <div className={mode === 'standalone' ? 'flex h-full items-center justify-center' : ''}>
        <MailOpen className="h-36 w-36 text-muted" />
      </div>
    );
  }

  if (!email) {
    return (
      <div
        className={
          mode === 'standalone'
            ? 'flex h-full items-center justify-center p-8 text-muted-foreground'
            : 'text-muted-foreground'
        }
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <Mail className="h-36 w-36 text-muted" />
        </div>
      </div>
    );
  }

  if (mode === 'embedded') {
    return (
      <EmailProductLineCreationProvider email={email as Email}>
      <EmailEventCreationProvider email={email as Email}>
      <EmailTaskCreationProvider email={email as Email} summary={summary}>
        <article
          className={cn(
            'glass-surface space-y-4 rounded-xl p-4 animate-fade-in-up',
            highlighted && 'ring-1 ring-primary/30 bg-accent/40',
          )}
        >
          <MessageMetadata
            email={email}
            variant="embedded"
            align={align}
            senderAvatarSrc={senderAvatarSrc}
            addressTooltipEntries={addressTooltipEntries}
            messageAddressEntries={messageAddressEntries}
            onArchive={handleArchive}
            onDelete={handleDelete}
            onReply={handleReplyAction}
            isParticipantContext={isParticipantContext}
            contactApprovalState={contactApprovalState}
            contactBadgeVariant={contactBadgeVariant}
            hasPgp={encryption.hasPgp}
            hasSmime={encryption.hasSmime}
            onSummarize={summaryAction}
            onRehydrate={canRehydrate ? handleRehydrateEmail : undefined}
            rehydrating={rehydrating}
            unsubscribeUrl={unsubscribeUrl}
            onAddSenderLabel={labelsVisible ? handleOpenLabelDialog : undefined}
            onViewOcrData={canViewOcrData ? handleViewOcrData : undefined}
            onBlockSender={canBlockApprovedSender ? handleBlockApprovedSender : undefined}
            onMoveToFolder={handleMoveToFolder}
            onMarkRead={accountId ? handleMarkRead : undefined}
            onCopyAiPayload={copyAiPayloadAction}
            canTrainSpam={canTrainSpam}
            canTrainGood={canTrainGood}
            isTrainingSpam={isTrainingSpam}
            isTrainingGood={isTrainingGood}
            onTrainSpam={trainAsSpam}
            onTrainGood={trainAsGood}
          />
          {labelsVisible && (
            <EmailLabelChips labels={email.autoLabels} className="px-1" onRemoveLabel={handleRemoveLabel} />
          )}
          {ocrPanel}

          {isSummaryVisible && summarySupported && (
            <AiSummaryCard
              annotation={summaryAnnotation}
              quickSummary={summaryQuickSummary}
              isLoading={summaryLoading}
              error={summaryError}
              isSupported={summarySupported}
              onGenerate={(options) => requestSummary(options)}
              onRetry={() => requestSummary({ force: true })}
            />
          )}

          {contactNoticeVisible && (
            <ContactApprovalBanner
              state={contactApprovalState}
              canApprove={canApproveSender}
              canBlock={canBlockSender}
              onApprove={handleApproveSender}
              onBlock={handleBlockSender}
              disabled={operationPending}
            />
          )}

          {inlineReplyContext && (
            <InlineReplyComposer
              key={`${inlineReplyContext.messageId ?? 'msg'}:${inlineReplyContext.mode}`}
              context={inlineReplyContext}
              accountId={accountId}
              onClose={() => setInlineReplyContext(null)}
              onSent={() => {
                setInlineReplyContext(null);
                onEmailUpdate?.();
              }}
              onOpenFullCompose={(replyContext) => {
                setInlineReplyContext(null);
                handleOpenFullComposer(replyContext);
              }}
            />
          )}

          <InlineImageControls visible={inlineImageControlsVisible} onAllowImages={remoteImages.toggleRemoteImages} />

          {email.calendarEvent && (
            <CalendarInvitation
              calendarEvent={email.calendarEvent}
              messageId={email.id}
              accountId={email.accountId}
              onCalendarEventUpdate={handleCalendarEventUpdate}
            />
          )}

          <AttachmentList
            attachments={attachmentsState.attachments}
            previewing={attachmentsState.previewing}
            downloading={attachmentsState.downloading}
            onTogglePreview={attachmentsState.handleTogglePreview}
            onDownload={attachmentsState.handleDownload}
            isPreviewable={attachmentsState.isPreviewable}
            align={align}
          />

          <MessageBody key={email.id} sanitizedHtml={sanitizedHtml} expandQuotedByDefault={isForwardedEmail} />
        </article>
      </EmailTaskCreationProvider>
      </EmailEventCreationProvider>
      </EmailProductLineCreationProvider>
    );
  }

  return (
    <EmailProductLineCreationProvider email={email as Email}>
    <EmailEventCreationProvider email={email as Email}>
    <EmailTaskCreationProvider email={email as Email} summary={summary}>
      <div className="glass-page flex h-full flex-col animate-fade-in-up">
      <DetailHeader
        email={email}
        onBack={onBack}
        hasPgp={encryption.hasPgp}
        hasSmime={encryption.hasSmime}
        isSent={isSent}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onReply={handleReplyAction}
        onSummarize={summaryAction}
        onRehydrate={canRehydrate ? handleRehydrateEmail : undefined}
        rehydrating={rehydrating}
        onMarkRead={accountId ? handleMarkRead : undefined}
        onAddLabel={labelsVisible ? handleOpenLabelDialog : undefined}
        onCopyAiPayload={copyAiPayloadAction}
        onViewOcrData={canViewOcrData ? handleViewOcrData : undefined}
        onBlockSender={canBlockApprovedSender ? handleBlockApprovedSender : undefined}
        onMoveToFolder={handleMoveToFolder}
      />
      <ScrollArea className="flex-1">
        {/* pb clears the floating mobile action bar until md, where it hides */}
        <div className="space-y-4 sm:space-y-6 px-4 pt-6 pb-28 sm:px-8 md:pb-8">
          {showThreadLoadError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn't load the conversation</AlertTitle>
              <AlertDescription className="flex items-center gap-3">
                <span>This message is part of a thread that failed to load.</span>
                <Button size="sm" variant="outline" onClick={handleRetryThread}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <MessageMetadata
            email={email}
            variant="standalone"
            align="start"
            senderAvatarSrc={senderAvatarSrc}
            addressTooltipEntries={addressTooltipEntries}
            messageAddressEntries={messageAddressEntries}
            onArchive={handleArchive}
            onDelete={handleDelete}
            onReply={handleReplyAction}
            isParticipantContext={false}
            contactApprovalState={null}
            contactBadgeVariant="default"
            hasPgp={encryption.hasPgp}
            hasSmime={encryption.hasSmime}
            onSummarize={summaryAction}
            onRehydrate={canRehydrate ? handleRehydrateEmail : undefined}
            rehydrating={rehydrating}
            unsubscribeUrl={unsubscribeUrl}
            onAddSenderLabel={labelsVisible ? handleOpenLabelDialog : undefined}
            onViewOcrData={canViewOcrData ? handleViewOcrData : undefined}
          />
          {labelsVisible && (
            <EmailLabelChips labels={email.autoLabels} onRemoveLabel={handleRemoveLabel} />
          )}
          {ocrPanel}

          {isSummaryVisible && summarySupported && (
            <AiSummaryCard
              annotation={summaryAnnotation}
              quickSummary={summaryQuickSummary}
              isLoading={summaryLoading}
              error={summaryError}
              isSupported={summarySupported}
              onGenerate={(options) => requestSummary(options)}
              onRetry={() => requestSummary({ force: true })}
            />
          )}

          {contactNoticeVisible && (
            <ContactApprovalBanner
              state={contactApprovalState}
              canApprove={canApproveSender}
              canBlock={canBlockSender}
              onApprove={handleApproveSender}
              onBlock={handleBlockSender}
              disabled={operationPending}
            />
          )}

          {inlineReplyContext && (
            <InlineReplyComposer
              key={`${inlineReplyContext.messageId ?? 'msg'}:${inlineReplyContext.mode}`}
              context={inlineReplyContext}
              accountId={accountId}
              onClose={() => setInlineReplyContext(null)}
              onSent={() => {
                setInlineReplyContext(null);
                onEmailUpdate?.();
              }}
              onOpenFullCompose={(replyContext) => {
                setInlineReplyContext(null);
                handleOpenFullComposer(replyContext);
              }}
            />
          )}

          <InlineImageControls visible={inlineImageControlsVisible} onAllowImages={remoteImages.toggleRemoteImages} />

          {email.calendarEvent && (
            <CalendarInvitation
              calendarEvent={email.calendarEvent}
              messageId={email.id}
              accountId={email.accountId}
              onCalendarEventUpdate={handleCalendarEventUpdate}
            />
          )}

          <AttachmentList
            attachments={attachmentsState.attachments}
            previewing={attachmentsState.previewing}
            downloading={attachmentsState.downloading}
            onTogglePreview={attachmentsState.handleTogglePreview}
            onDownload={attachmentsState.handleDownload}
            isPreviewable={attachmentsState.isPreviewable}
          />

            {linkedTasks.length > 0 && <EmailTodoLinks key={email.id} tasks={linkedTasks} />}

          {/* Related tasks based on email content */}
          {accountId && (
            <RelatedTasks accountId={accountId} messageId={email.id} limit={5} />
          )}

          <div className="rounded-lg bg-white p-4">
            {loading && !email.bodyHtml && !email.body ? (
              // P14: body still fetching behind an optimistic header — skeleton
              // just this region rather than blanking the whole pane. Review
              // finding 7: only skeleton when the body is ACTUALLY absent — if a
              // hover prefetch (P15) already merged it into the seed, show it
              // immediately instead of hiding it behind the skeleton for an RTT.
              <div className="min-h-[200px] animate-pulse space-y-3" aria-hidden="true">
                <div className="h-4 w-3/4 rounded bg-muted" />
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-5/6 rounded bg-muted" />
                <div className="h-4 w-2/3 rounded bg-muted" />
              </div>
            ) : (
              <MessageBody
                key={email.id}
                sanitizedHtml={sanitizedHtml}
                minHeight="min-h-[200px]"
                expandQuotedByDefault={isForwardedEmail}
              />
            )}
          </div>
        </div>
      </ScrollArea>
      <MobileActionBar
        emailId={email.id}
        isRead={email.read}
        onArchive={handleArchive}
        onDelete={handleDelete}
        onReply={handleReplyAction}
        onMarkRead={accountId ? handleMarkRead : undefined}
        canTrainSpam={canTrainSpam}
        canTrainGood={canTrainGood}
        isTrainingSpam={isTrainingSpam}
        isTrainingGood={isTrainingGood}
        onTrainSpam={trainAsSpam}
        onTrainGood={trainAsGood}
        onSummarize={summaryAction}
        onRehydrate={canRehydrate ? handleRehydrateEmail : undefined}
        rehydrating={rehydrating}
        onAddLabel={labelsVisible ? handleOpenLabelDialog : undefined}
        onCopyAiPayload={copyAiPayloadAction}
        onViewOcrData={canViewOcrData ? handleViewOcrData : undefined}
        onBlockSender={canBlockApprovedSender ? handleBlockApprovedSender : undefined}
        onMoveToFolder={handleMoveToFolder}
      />
      <AddSenderToLabelDialog
        open={labelDialogOpen}
        onOpenChange={(open) => {
          setLabelDialogOpen(open);
          if (!open) {
            setLabelDialogError(null);
          }
        }}
        labels={senderLabels}
        selectedLabelId={selectedLabelId}
        onSelectLabel={setSelectedLabelId}
        onConfirm={handleAddLabelConfirm}
        isSubmitting={labelDialogPending}
        error={labelDialogError}
      />
  </div>
  </EmailTaskCreationProvider>
  </EmailEventCreationProvider>
  </EmailProductLineCreationProvider>
  );
}, arePropsEqual);

function arePropsEqual(prevProps: EmailDetailProps, nextProps: EmailDetailProps) {
  // Compare EVERY prop, callbacks included (D7): a parent recreates a handler
  // exactly when its dependencies changed, and rendering with the stale
  // closure was the bug the old field-whitelist comparator allowed. Parents
  // pass useCallback-stable handlers, so this remains an effective memo.
  const keys = new Set([
    ...Object.keys(prevProps),
    ...Object.keys(nextProps),
  ]) as Set<keyof EmailDetailProps>;
  for (const key of keys) {
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }
  return true;
}

function formatAddressDisplay(entry?: { email: string; name?: string | null }): string {
  if (!entry || !entry.email) {
    return '';
  }
  const trimmedName = entry.name?.trim();
  if (trimmedName && trimmedName.length > 0 && trimmedName !== entry.email) {
    return `${trimmedName} <${entry.email}>`;
  }
  return entry.email;
}

function formatAddressList(entries?: Array<{ email: string; name?: string | null }>): string {
  if (!entries || entries.length === 0) {
    return '';
  }
  return entries
    .map((entry) => formatAddressDisplay(entry))
    .filter(Boolean)
    .join(', ');
}

const AI_PAYLOAD_MAX_CHARS = 200_000;

function buildAiDebugPayload(email: EmailLike): string {
  const headerLines = [`Subject: ${email.subject || '(no subject)'}`];
  const fromValue = formatAddressDisplay(email.from);
  if (fromValue) {
    headerLines.push(`From: ${fromValue}`);
  }
  const toValue = formatAddressList(email.to);
  if (toValue) {
    headerLines.push(`To: ${toValue}`);
  }
  const ccValue = formatAddressList(email.cc);
  if (ccValue) {
    headerLines.push(`Cc: ${ccValue}`);
  }
  const sentAt = toIsoString(email.date);
  if (sentAt) {
    headerLines.push(`Date: ${sentAt}`);
  }
  const bodyText = extractPlainBodyText(email);
  return `${headerLines.join('\n')}`.concat(`\n\nEmail Body:\n${bodyText}`);
}

function toIsoString(value: Date | string | number | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

function extractPlainBodyText(email: EmailLike): string {
  const textBody = collapseWhitespace(email.body ?? '');
  let content = '';
  if (textBody) {
    content = textBody;
  } else if (email.bodyHtml) {
    const stripped = collapseWhitespace(stripHtmlTags(email.bodyHtml));
    content = stripped || '';
  }
  // No snippet fallback - if no body available, content stays empty
  const ocrText = collapseWhitespace(email.ocrText ?? '');
  if (ocrText) {
    const ocrBlock = `[Image text]\n${ocrText}`;
    content = content ? `${content}\n\n${ocrBlock}` : ocrBlock;
  }
  return truncateText(content, AI_PAYLOAD_MAX_CHARS);
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  if (typeof document === 'undefined') {
    throw new Error('Clipboard API unavailable');
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

function stripHtmlTags(value: string): string {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function collapseWhitespace(value?: string): string {
  if (!value) {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim();
}

function truncateText(value: string, maxChars: number): string {
  if (!value) {
    return '';
  }
  if (!Number.isFinite(maxChars) || value.length <= maxChars) {
    return value;
  }
  return `${value.slice(0, Math.max(0, maxChars - 3))}...`;
}

function extractEncryptionFlags(email: EmailLike | null) {
  if (!email || !email.headers) {
    return { hasPgp: false, hasSmime: false };
  }
  const contentType = String(email.headers['content-type'] || email.headers['Content-Type'] || '').toLowerCase();
  return {
    hasPgp: contentType.includes('pgp') || contentType.includes('application/pgp'),
    hasSmime: contentType.includes('pkcs7') || contentType.includes('smime'),
  };
}

function formatOcrReason(value?: string | null): string | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  switch (normalized) {
    case 'below_min_bytes':
      return 'image size below minimum bytes';
    case 'below_min_dimensions':
      return 'image dimensions below minimum';
    case 'ocr_disabled':
      return 'ocr disabled';
    default: {
      const spaced = normalized.replace(/[_-]+/g, ' ');
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    }
  }
}

function formatReasonCounts(reasons: Record<string, number>): string {
  const entries = Object.entries(reasons).filter(([, count]) => count > 0);
  if (!entries.length) {
    return '';
  }
  return entries
    .sort(([, aCount], [, bCount]) => bCount - aCount)
    .map(([reason, count]) => {
      const label = formatOcrReason(reason) ?? reason;
      return label ? (count > 1 ? `${label} (${count})` : label) : '';
    })
    .filter(Boolean)
    .join(', ');
}

interface ContactApprovalBannerProps {
  state: EmailLike['contactApprovalState'] | null;
  canApprove: boolean;
  canBlock: boolean;
  onApprove?: () => void;
  onBlock?: () => void;
  disabled?: boolean;
}

interface EmailTodoLinksProps {
  tasks: TodoTask[];
}

const HIDDEN_TASK_STATUSES: Array<TodoTask['status']> = ['completed', 'cancelled'];

function EmailTodoLinks({ tasks }: EmailTodoLinksProps) {
  const { setTaskStatus } = useTodoActionsStore();
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  const [showHiddenTasks, setShowHiddenTasks] = useState(false);

  const { visibleTasks, hiddenTasks } = useMemo(() => {
    return tasks.reduce(
      (acc, task) => {
        if (HIDDEN_TASK_STATUSES.includes(task.status)) {
          acc.hiddenTasks.push(task);
        } else {
          acc.visibleTasks.push(task);
        }
        return acc;
      },
      { visibleTasks: [] as TodoTask[], hiddenTasks: [] as TodoTask[] },
    );
  }, [tasks]);

  const displayedTasks = showHiddenTasks ? tasks : visibleTasks;
  const hiddenTaskCount = hiddenTasks.length;
  const hasHiddenTasks = hiddenTaskCount > 0;

  const handleTaskInteract = useCallback((taskId: string, mode: 'primary' | 'toggle' | 'range') => {
    if (mode === 'primary') {
      setFocusedTaskId((prev) => (prev === taskId ? null : taskId));
    }
  }, []);

  const handleSetStatus = useCallback(
    (taskId: string, status: TodoTask['status']) => {
      void setTaskStatus(taskId, status);
    },
    [setTaskStatus]
  );

  return (
    <div className="rounded-2xl border border-border/60 bg-card/80 p-4 space-y-1">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Linked Tasks</h3>
        </div>
        {hasHiddenTasks && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowHiddenTasks((prev) => !prev)}
            className="text-primary"
          >
            {showHiddenTasks
              ? 'Hide completed/cancelled'
              : `Show ${hiddenTaskCount} completed/cancelled`}
          </Button>
        )}
      </div>
      {displayedTasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {hasHiddenTasks ? 'No active tasks. Use the button above to show completed or cancelled tasks.' : 'No tasks linked yet.'}
        </p>
      ) : (
        <DndContext>
          <SortableContext items={displayedTasks.map((task) => `task-${task.id}`)} strategy={verticalListSortingStrategy}>
            <div className={displayedTasks.length > 1 ? "flex flex-col xl:grid xl:grid-cols-2 gap-2" : "flex flex-col gap-2"}>
              {displayedTasks.map((task) => (
                <TodoTaskCard
                  key={task.id}
                  task={task}
                  focused={focusedTaskId === task.id}
                  multiSelected={false}
                  onInteract={handleTaskInteract}
                  onComplete={(taskId) => handleSetStatus(taskId, 'completed')}
                  onCancel={(taskId) => handleSetStatus(taskId, 'cancelled')}
                  onDelete={(taskId) => handleSetStatus(taskId, 'deleted')}
                  showListContext
                  hideScheduledDate
                  activeListId={task.listId}
                  activeProjectId={task.projectId}
                  disableDrag
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function ContactApprovalBanner({
  state,
  canApprove,
  canBlock,
  onApprove,
  onBlock,
  disabled = false,
}: ContactApprovalBannerProps) {
  if (!state || state === 'approved') {
    return null;
  }

  const isBlocked = state === 'blocked';
  const variant =
    state === 'blocked'
      ? 'border border-destructive/30 bg-destructive/10'
      : 'border border-primary/30 bg-primary/10';

  return (
    <div
      className={cn(
        'mt-3 flex flex-col gap-3 rounded-lg px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between',
        variant,
      )}
    >
      <div className="flex items-start gap-2 text-left">
        <AlertCircle
          className={cn('h-5 w-5 flex-shrink-0', isBlocked ? 'text-destructive' : 'text-primary')}
        />
        <div className="space-y-1">
          <p className="font-semibold">
            {isBlocked ? 'This sender is blocked' : 'This sender is pending approval'}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canApprove && onApprove && (
          <Button size="sm" onClick={onApprove} className="gap-1" disabled={disabled}>
            <ShieldCheck className="h-4 w-4" />
            Approve
          </Button>
        )}
        {state === 'pending' && canBlock && onBlock && (
          <Button size="sm" variant="outline" onClick={onBlock} className="gap-1" disabled={disabled}>
            <ShieldOff className="h-4 w-4" />
            Block
          </Button>
        )}
      </div>
    </div>
  );
}

function EmailLabelChips({
  labels,
  className,
  onRemoveLabel,
}: {
  labels: Email['autoLabels'];
  className?: string;
  onRemoveLabel?: (labelId: string) => void;
}) {
  if (!labels?.length) {
    return null;
  }
  return (
    <div className={cn('flex flex-wrap gap-2 text-xs text-muted-foreground', className)}>
      {labels.map((label) => (
        <AutoLabelBadge
          key={label.id}
          label={label}
          reason={label.reason}
          showRemove={Boolean(onRemoveLabel && label.id)}
          onRemove={label.id ? () => onRemoveLabel?.(label.id!) : undefined}
        />
      ))}
    </div>
  );
}
